/**
 * «نقش‌ها و بازه‌ها» — جایگزینِ تبِ جدای «شغل‌های دیگر» و مودالِ «بازه‌های نقش» (v2.108.0).
 *
 * خواستهٔ کاربر (۱۴۰۵/۰۷/۰۱): «نوع همکاری و شغل‌های دیگر رو باید ادغام بکنیم… یکی از یه
 * تایمی روزمزد شده ولی تو اون بازه نمیاد محاسبه کنه… اگر بازه‌ای می‌زنیم فقط اون بازه…
 * تیکِ محاسبهٔ تکی داشته باشه؛ اگر خورد فقط اون بازه، اگر نخورد ترکیبی».
 *
 * مدل:
 *  • «پایه» همان نوع همکاری و مبالغِ بالای همین تب است — برای هر روزی که بازهٔ تکی ندارد.
 *  • هر ردیفِ اینجا یک نقش با **بازهٔ تاریخ** و **مدلِ نرخ** است:
 *      - تکی ✔  : در این روزها **فقط** همین حساب می‌شود (پایه و کارمزدِ پایه نه).
 *                 مثلاً ناجیه: کارمزدی، از ۲۲ام «روزمزدِ تکی».
 *      - ترکیبی  : روی پایه سوار می‌شود. مثلاً خادمعلی: ثابت + «سرایداری».
 *  • روزمزدِ کسی که «حضور و غیاب دستگاه» ندارد = همهٔ روزهای بازه؛ کم‌وزیاد در حقوق.
 *
 * زیرِ کاپوت همان `EmployeePayComponent` با `params.secondary = true` (+ `exclusive`) است.
 * ردیف‌های قدیمیِ «کارمزدی» بدونِ کلید، تکی خوانده می‌شوند — همان رفتارِ قبلی.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Button, Checkbox, Empty, Popconfirm, Segmented, Select, Space, Table, Tag, Tooltip,
  Typography, message,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import Alert from "../../components/GxAlert";
import GxNote from "../../components/GxNote";
import {
  addPayComponent, deactivatePayComponent, getEmployeePayPlan, getPayComponents,
  updatePayComponent,
} from "../../api/client";
import { apiError } from "../../utils/apiError";
import {
  JALALI_MONTHS, JalaliDateInput, currentJalali, toJalali, todayISO,
} from "../../utils/dateUtils";
import { faDigits, faNum } from "../../utils/faNumberFormat";
import { MoneyInput, formatMoney } from "../../utils/money";

const { Text } = Typography;

/** نقش‌های متداول — عنوانِ آزاد هم پذیرفته می‌شود */
export const ROLE_TITLES = [
  "روزمزد", "حقوق ثابت", "کارمزدی", "نگهبانی", "سرایداری", "وسط‌کاری",
  "انباردار", "نظافت", "سرپرستی خط", "برشکاری",
];

const RATE_TYPES = [
  { value: "daily", label: "روزمزد" },
  { value: "monthly", label: "ماهانه" },
  { value: "piece", label: "کارمزدی" },
  { value: "hourly", label: "ساعتی" },
];
const RATE_LABEL = Object.fromEntries(RATE_TYPES.map((r) => [r.value, r.label]));
const RATE_HINT = {
  monthly: "مبلغِ ماه؛ اگر بازه بخشی از ماه باشد، به‌همان نسبتِ روزها پرداخت می‌شود.",
  daily: "نرخ × روزها. بدونِ «حضور و غیاب دستگاه» همهٔ روزهای بازه حساب می‌شود (کم‌وزیاد در مرحلهٔ حقوق)؛ با تیکِ دستگاه، روزهای حاضر.",
  hourly: "نرخ × ساعتِ کارکردِ دستگاه در بازه (فقط برای پرسنلِ حضوری).",
  piece: "مبلغ ندارد: کارِ تولید/برشِ ثبت‌شده در این بازه طبقِ نرخ‌نامه حساب می‌شود.",
};
const PAY_MODEL_LABEL = { fixed: "حقوق ثابت", daily: "حقوق ثابت", piece: "کارمزدی", none: "بدون پایه" };

const roleTitle = (r) => r.params?.job_title || r.params?.title || r.component_type;
/** همان قاعدهٔ بک‌اند: ردیفِ قدیمیِ «کارمزدی» بدونِ کلید = تکی */
const isExclusive = (p = {}) => (p.exclusive == null ? p.rate_type === "piece" : !!p.exclusive);

export default function PayRolesEditor({ employeeId, hireDate }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);

  const [role, setRole] = useState(null);
  const [rateType, setRateType] = useState("daily");
  const [amount, setAmount] = useState(null);
  const [monthly, setMonthly] = useState(null);      // کمکی: پایهٔ ماهانه ⇒ نرخِ روزانه
  const [from, setFrom] = useState(todayISO());
  const [to, setTo] = useState(null);
  const [exclusive, setExclusive] = useState(true);

  const cj = currentJalali();
  const [ym, setYm] = useState({ year: cj.year, month: cj.month });
  const [plan, setPlan] = useState(null);

  const reset = () => {
    setEditId(null); setRole(null); setAmount(null); setMonthly(null);
    setRateType("daily"); setFrom(todayISO()); setTo(null); setExclusive(true);
  };

  const load = useCallback(async () => {
    if (!employeeId) { setRows([]); return; }
    setLoading(true);
    try {
      const [r, p] = await Promise.all([
        getPayComponents(employeeId, { active_only: true }),
        getEmployeePayPlan(employeeId, ym.year, ym.month).catch(() => ({ data: null })),
      ]);
      setRows((r.data || []).filter((c) => c.params?.secondary));
      setPlan(p.data);
    } catch { /* نبودِ نقش خطا نیست */ }
    setLoading(false);
  }, [employeeId, ym]);
  useEffect(() => { load(); }, [load]);

  const startEdit = (r) => {
    setEditId(r.id);
    setRole(roleTitle(r));
    setRateType(r.params?.rate_type || "monthly");
    setAmount(r.params?.amount ?? null);
    setMonthly(null);
    setFrom(r.effective_from || todayISO());
    setTo(r.effective_to || null);
    setExclusive(isExclusive(r.params));
  };

  const isPiece = rateType === "piece";
  const submit = async () => {
    if (!role) return message.warning("عنوانِ نقش را انتخاب یا تایپ کنید");
    if (!isPiece && !(amount > 0)) return message.warning("مبلغ/نرخ را وارد کنید");
    if (!from) return message.warning("تاریخِ شروع لازم است");
    if (to && to < from) return message.warning("تاریخ پایان قبل از شروع است");
    setSaving(true);
    try {
      const params = { job_title: role, rate_type: rateType, exclusive,
        amount: isPiece ? null : amount };
      if (editId) {
        await updatePayComponent(employeeId, editId, {
          params, effective_from: from,
          ...(to ? { effective_to: to } : { clear_effective_to: true }),
        });
        message.success("ویرایش شد");
      } else {
        await addPayComponent(employeeId, {
          component_type: "allowance",
          params: { secondary: true, ...params, ...(isPiece ? { amount: undefined } : {}) },
          effective_from: from, effective_to: to || null,
        });
        message.success(`نقشِ «${role}» اضافه شد`);
      }
      reset();
      await load();
    } catch (e) {
      message.error(apiError(e, "ذخیره نشد"));
    }
    setSaving(false);
  };

  if (!employeeId) {
    return (
      <Alert type="info" showIcon message="اول پرسنل را ذخیره کنید"
        description="نقش‌ها و بازه‌ها بعد از ساختِ پرسنل، از همین تب (ویرایش) اضافه می‌شوند." />
    );
  }

  const label = (t) => <Text type="secondary" style={{ fontSize: 12 }}>{t}</Text>;

  return (
    <>
      <GxNote id="pay-roles" signature="v2112"
        message="«نوع همکاری» = مدلِ همیشگی؛ «نقش‌ها و بازه‌ها» = استثنا با تاریخ. بازه همیشه بر نوع همکاری مقدم است">
        <div>• <b>نوع همکاری</b> (تیک‌های بالا) برای هر روزی است که بازهٔ <b>تکی</b> ندارد. تیکِ «کارمزد دوخت» یعنی
          کارمزدِ تولیدِ <b>همان روزها</b> حساب شود.</div>
        <div>• بازهٔ <b>تکی</b> = در آن روزها <b>فقط</b> همین مدل (پایه و کارمزدِ پایه نه). مثلاً کارمزدی‌ای که از یک تاریخ روزمزد شده.</div>
        <div>• بازهٔ <b>ترکیبی</b> = روی پایه اضافه می‌شود (سرایداری کنارِ وسط‌کاری).</div>
        <div>• بازهٔ <b>کارمزدی</b> کارمزدِ تولیدِ داخلِ بازه را <b>حتی اگر تیکِ «کارمزد دوخت» خاموش باشد</b> حساب می‌کند؛
          اگر تیک روشن باشد اضافی است ولی دوبار شمرده نمی‌شود. خلاصه: <b>اگر همیشه کارمزدی است تیک را بزنید؛
          اگر فقط چند روز، تیک را نزنید و بازهٔ کارمزدی بدهید.</b></div>
        <div>• پایینِ همین بخش «این ماه چطور حساب می‌شود» همهٔ این‌ها را برای ماهِ انتخابی به زبانِ ساده می‌گوید.</div>
      </GxNote>

      <Space wrap align="end" style={{ marginBottom: 6 }}>
        <div>
          {label("عنوان")}
          <div>
            <Select showSearch style={{ width: 150 }} placeholder="مثلاً روزمزد"
              mode="tags" maxTagCount={1}
              options={ROLE_TITLES.map((r) => ({ value: r, label: r }))}
              value={role ? [role] : []}
              onChange={(arr) => {
                const v = Array.isArray(arr) && arr.length ? String(arr[arr.length - 1]).trim() : null;
                setRole(v);
                // عنوانِ مدل‌دار، مدلِ نرخ را هم پیشنهاد می‌دهد
                if (v === "روزمزد") setRateType("daily");
                else if (v === "کارمزدی") setRateType("piece");
                else if (v === "حقوق ثابت") setRateType("monthly");
              }} />
          </div>
        </div>
        <div>
          {label("مدل نرخ")}
          <div><Segmented value={rateType} onChange={setRateType} options={RATE_TYPES} /></div>
        </div>
        {!isPiece && (
          <div>
            {label(rateType === "monthly" ? "مبلغ ماهانه (ریال)"
              : rateType === "daily" ? "نرخ هر روز (ریال)" : "نرخ هر ساعت (ریال)")}
            <div><MoneyInput value={amount} onChange={setAmount} style={{ width: 150 }} /></div>
          </div>
        )}
        {rateType === "daily" && (
          <div>
            {label("یا پایهٔ ماهانه ÷ ۳۰")}
            <div>
              <MoneyInput value={monthly} placeholder="پایهٔ ماهانه" style={{ width: 150 }}
                onChange={(v) => { setMonthly(v); if (v > 0) setAmount(Math.round(v / 30)); }} />
            </div>
          </div>
        )}
        <div>
          {label("از تاریخ")}
          <div><JalaliDateInput value={from} onChange={setFrom} /></div>
        </div>
        <div>
          {label("تا تاریخ (اختیاری)")}
          <div><JalaliDateInput value={to} allowClear onChange={setTo} /></div>
        </div>
        <Tooltip title="تیک‌خورده: در این بازه فقط همین حساب می‌شود. بدونِ تیک: روی پایه اضافه می‌شود.">
          <Checkbox checked={exclusive} onChange={(e) => setExclusive(e.target.checked)}
            style={{ paddingBottom: 6 }}>
            <b>تکی</b> (فقط همین در این بازه)
          </Checkbox>
        </Tooltip>
        <Button type="primary" icon={editId ? <EditOutlined /> : <PlusOutlined />}
          loading={saving} onClick={submit}>
          {editId ? "ذخیرهٔ تغییر" : "افزودن"}
        </Button>
        {editId && <Button onClick={reset}>انصراف</Button>}
      </Space>
      <div style={{ marginBottom: 10 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>{RATE_HINT[rateType]}</Text>
        {hireDate && !editId && (
          <Button type="link" size="small" onClick={() => setFrom(hireDate)}>
            از تاریخِ استخدام ({toJalali(hireDate)})
          </Button>
        )}
      </div>

      <Table size="small" rowKey="id" loading={loading} pagination={false} dataSource={rows}
        scroll={{ x: "max-content" }}
        locale={{ emptyText: <Empty description="نقش یا بازه‌ای ثبت نشده — فقط پایه حساب می‌شود" /> }}
        columns={[
          { title: "عنوان", render: (_, r) => <b>{roleTitle(r)}</b> },
          { title: "نوع", width: 90,
            render: (_, r) => (isExclusive(r.params)
              ? <Tag color="volcano">تکی</Tag> : <Tag color="blue">ترکیبی</Tag>) },
          { title: "مدل نرخ", width: 90,
            render: (_, r) => <Tag>{RATE_LABEL[r.params?.rate_type || "monthly"]}</Tag> },
          { title: "مبلغ (ریال)", align: "left",
            render: (_, r) => (r.params?.rate_type === "piece"
              ? <Text type="secondary">طبق نرخ‌نامه</Text>
              : <b className="gx-money">{formatMoney(r.params?.amount)}</b>) },
          { title: "از", dataIndex: "effective_from", render: (v) => toJalali(v) },
          { title: "تا", dataIndex: "effective_to",
            render: (v) => (v ? toJalali(v) : <Text type="secondary">ادامه دارد</Text>) },
          { title: "", width: 80,
            render: (_, r) => (
              <Space size={2}>
                <Tooltip title="ویرایش">
                  <Button size="small" type="text" icon={<EditOutlined />} onClick={() => startEdit(r)} />
                </Tooltip>
                <Popconfirm title="این نقش غیرفعال شود؟"
                  description="سابقهٔ ماه‌های بسته دست‌نخورده می‌ماند."
                  okText="بله" cancelText="خیر"
                  onConfirm={async () => {
                    await deactivatePayComponent(employeeId, r.id);
                    message.success("غیرفعال شد");
                    if (editId === r.id) reset();
                    load();
                  }}>
                  <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ) },
        ]} />

      <PlanPreview plan={plan} ym={ym} setYm={setYm} onReload={load} />
    </>
  );
}

/** «این ماه چطور حساب می‌شود» — همان خروجیِ موتور، قبل از محاسبهٔ حقوق. */
export function PlanPreview({ plan, ym, setYm, onReload, compact = false }) {
  if (!plan) return null;
  const base = plan.base || {};
  return (
    <div style={{ marginTop: 12, border: "1px solid var(--gx-border, #eee)", borderRadius: 8,
                  padding: "8px 12px", background: "var(--gx-surface-2, #fafafa)" }}>
      <Space wrap style={{ marginBottom: 6 }}>
        <b>{compact ? "نقش‌ها و بازه‌های این ماه" : "این ماه چطور حساب می‌شود؟"}</b>
        {setYm && (
          <Select size="small" value={ym.month} style={{ width: 100 }}
            onChange={(m) => setYm({ ...ym, month: m })}
            options={JALALI_MONTHS.map((m, i) => ({ value: i + 1, label: m }))} />
        )}
        {setYm && (
          <Select size="small" value={ym.year} style={{ width: 84 }}
            onChange={(y) => setYm({ ...ym, year: y })}
            options={[ym.year - 1, ym.year, ym.year + 1].map((y) => ({ value: y, label: faDigits(y) }))} />
        )}
        {onReload && <Button size="small" icon={<ReloadOutlined />} onClick={onReload} />}
      </Space>
      <div style={{ fontSize: 12, lineHeight: 2 }}>
        <div>
          <Tag color="green">پایه</Tag>
          {PAY_MODEL_LABEL[base.pay_model] || base.pay_model || "—"}
          {" — "}{faNum(base.days)} روز از {faNum(plan.month_days)}
          {base.piece && <Text type="secondary"> (کارمزدِ تولیدِ همین روزها حساب می‌شود)</Text>}
        </div>
        {(plan.ranges || []).map((r) => (
          <div key={r.id}>
            <Tag color={r.exclusive ? "volcano" : "blue"}>{r.exclusive ? "تکی" : "ترکیبی"}</Tag>
            <b>{r.title}</b> ({r.rate_label}) — {faDigits(r.label)}: {faDigits(r.basis)}
            {r.rate_type !== "piece" && <> = <b className="gx-money">{formatMoney(r.amount)}</b> ریال</>}
            {(r.absence_deduction > 0 || r.shortage_deduction > 0) && (
              <Text type="danger"> (کسرِ حضور {formatMoney(r.absence_deduction + r.shortage_deduction)})</Text>
            )}
            {r.warning && <div><Text type="warning">{r.warning}</Text></div>}
          </div>
        ))}
        {!(plan.ranges || []).length && <Text type="secondary">بازه‌ای در این ماه نیست — کلِ ماه با پایه.</Text>}
        {(plan.notes || []).map((n, i) => (
          <div key={i} style={{ marginTop: 4, color: "var(--gx-info, #1677ff)" }}>ⓘ {faDigits(n)}</div>
        ))}
      </div>
    </div>
  );
}
